import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useData } from "../context/DataContext";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Combobox } from "../components/ui/combobox";
import { PersonSelect, PersonOption } from "../components/ui/person-select";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { validatePassword, PasswordStrength, defaultRequirements } from "../utils/password-validation";
import { PasswordStrengthIndicator } from "../components/PasswordStrengthIndicator";
import { PasswordRequirements } from "../components/PasswordRequirements";
import { JobRoleTitle, UserRole, NewUser } from "../types/index";
import type { SubcontractorVendorOption } from "../types/lists.types";
import api from "../utils/api";
import { EMERGENCY_CONTACT_RELATIONSHIP_OPTIONS } from "../constants/person.constants";
import {
  GENDER_OPTIONS,
  INDIGENOUS_STATUS_OPTIONS,
  COUNTRY_OF_BIRTH_OPTIONS,
  DISABILITY_STATUS_OPTIONS,
  WORK_LOCATION_OPTIONS,
} from "../constants/demographics.constants";
import { ChevronLeft, Loader2, Eye, EyeOff } from "lucide-react";


export default function AddPersonPage() {
  const navigate = useNavigate();
  const { departments, fetchManagerUsers, managerUsers, addUser, jobRoles, employmentEntities, fetchEmploymentEntities, userRoles } = useData();
  const { currentUser } = useAuth();

  const [isLoading, setIsLoading] = useState(false);
  const [showPasswordRequirements, setShowPasswordRequirements] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [newUser, setNewUser] = useState<NewUser & { showInAttendance?: boolean; accrualStartDate?: string }>({
    email: "",
    password: "",
    name: "",
    userRole: "Worker",
    jobRole: "" as JobRoleTitle,
    department: "",
    mobile: "",
    manager: null,
    dateOfBirth: "",
    startDate: "",
    showInAttendance: false,
    accrualStartDate: "",
    employmentStatus: "",
    employmentEntity: null as number | null,
    gender: "",
    indigenousStatus: "",
    countryOfBirth: "",
    disabilityStatus: "",
    workLocation: "",
    isSubcontractor: null as boolean | null,
    subcontractorVendorId: null,
    residentialAddress: "",
    emergencyContactName: "",
    emergencyContactNumber: "",
    emergencyContactRelationship: ""
  });

  const [newPasswordValidation, setNewPasswordValidation] = useState(() =>
    validatePassword(newUser.password || "")
  );
  const [vendors, setVendors] = useState<SubcontractorVendorOption[]>([]);

  useEffect(() => {
    if (managerUsers.length === 0) fetchManagerUsers();
    if (employmentEntities.length === 0) fetchEmploymentEntities();
    api
      .get<{ success: boolean; vendors: SubcontractorVendorOption[] }>('/api/users/subcontractor-vendors')
      .then((response) => setVendors(response.vendors))
      .catch(() => setVendors([]));
  }, []);

  useEffect(() => {
    setNewPasswordValidation(validatePassword(newUser.password || ""));
  }, [newUser.password]);

  const passwordRequirements = [
    `At least ${defaultRequirements.minLength} characters`,
    "At least one lowercase letter",
    "At least one uppercase letter",
    "At least one number",
    "At least one special character"
  ];

  const newPasswordMetRequirements = [
    (newUser.password || "").length >= defaultRequirements.minLength,
    /[a-z]/.test(newUser.password || ""),
    /[A-Z]/.test(newUser.password || ""),
    /\d/.test(newUser.password || ""),
    /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(newUser.password || "")
  ];

  const availableJobRoles = useMemo(() => {
    const uniqueJobRoles = new Map();
    jobRoles.forEach(role => {
      if (role.archived) return;
      const normalizedTitle = role.title.trim().toLowerCase();
      if (!uniqueJobRoles.has(normalizedTitle)) {
        uniqueJobRoles.set(normalizedTitle, {
          id: role.id,
          title: role.title,
          departmentId: role.departmentId
        });
      }
    });
    const uniqueRolesArray = Array.from(uniqueJobRoles.values());
    return uniqueRolesArray.sort((a, b) => a.title.localeCompare(b.title));
  }, [jobRoles]);

  // Values keep the raw department id — the same identifier the old Select
  // items carried — so the payload submitted on create is unchanged.
  const departmentOptions = useMemo(
    () =>
      departments.map((dept) => ({
        label: `${dept.departmentCode} - ${dept.departmentName}`,
        value: dept.id,
      })),
    [departments]
  );

  const jobRoleOptions = useMemo(
    () => availableJobRoles.map((role) => ({ label: role.title, value: role.title })),
    [availableJobRoles]
  );

  const vendorOptions = useMemo(
    () =>
      vendors.map((vendor) => ({
        label: `${vendor.tradingName || vendor.legalName}${vendor.isActive ? "" : " (Inactive)"}`,
        value: String(vendor.id),
      })),
    [vendors]
  );

  const managerOptions = useMemo<PersonOption[]>(
    () =>
      managerUsers.map((u) => ({
        id: Number(u.id),
        name: u.name,
        email: u.email,
        jobRole: u.jobRole || null,
        department:
          u.department != null && typeof u.department === "object"
            ? { id: Number(u.department.id), departmentName: u.department.departmentName }
            : null,
      })),
    [managerUsers]
  );

  if (currentUser?.userRole !== "Admin" && currentUser?.userRole !== "Director" && currentUser?.userRole !== "Manager") {
    return (
      <div className="flex items-center justify-center h-full">
        <p>You don't have permission to access this page</p>
      </div>
    );
  }

  const handleAdd = async () => {
    if (!newUser.email || !newUser.name || !newUser.department || !newUser.userRole || !newUser.jobRole || !newUser.password || !newUser.startDate) {
      toast.error("Required fields must be filled");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newUser.email)) {
      toast.error("Please enter a valid email address");
      return;
    }

    if (newUser.userRole !== "Admin" && !newUser.manager) {
      toast.error("Manager is required for non-Admin roles");
      return;
    }

    if (newUser.isSubcontractor && !newUser.subcontractorVendorId) {
      toast.error("Select a subcontractor vendor");
      return;
    }

    if (newUser.password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    if (!newPasswordValidation.isValid) {
      toast.error("Password does not meet the security requirements");
      setShowPasswordRequirements(true);
      return;
    }

    const userToAdd = {
      email: newUser.email,
      password: newUser.password,
      mobile: newUser.mobile || undefined,
      jobRole: newUser.jobRole,
      department: newUser.department,
      userRole: newUser.userRole,
      name: newUser.name,
      manager: newUser.manager || null,
      dateOfBirth: newUser.dateOfBirth || undefined,
      startDate: newUser.startDate || undefined,
      showInAttendance: newUser.showInAttendance,
      accrualStartDate: newUser.showInAttendance ? (newUser.accrualStartDate || undefined) : undefined,
      employmentStatus: newUser.employmentStatus || undefined,
      employmentEntity: newUser.employmentEntity || undefined,
      gender: newUser.gender || null,
      indigenousStatus: newUser.indigenousStatus || null,
      countryOfBirth: newUser.countryOfBirth || null,
      disabilityStatus: newUser.disabilityStatus || null,
      workLocation: newUser.workLocation || null,
      isSubcontractor: newUser.isSubcontractor ?? null,
      subcontractorVendorId: newUser.isSubcontractor ? newUser.subcontractorVendorId : null,
      residentialAddress: newUser.residentialAddress || undefined,
      emergencyContactName: newUser.emergencyContactName || undefined,
      emergencyContactNumber: newUser.emergencyContactNumber || undefined,
      emergencyContactRelationship: newUser.emergencyContactRelationship || undefined
    };

    try {
      setIsLoading(true);
      await addUser(userToAdd);
      await fetchManagerUsers();
      navigate("/people");
    } catch (error) {
      console.error("Error adding user:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="outline" size="icon" onClick={() => navigate(-1)}>
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <h1 className="text-2xl font-bold">Add New Person</h1>
      </div>

      <Card>
        <CardContent className="pt-6 space-y-6">
          {/* Hidden decoy field to prevent browser password autofill */}
          <input
            type="password"
            autoComplete="new-password"
            tabIndex={-1}
            style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0, width: 0 }}
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="name">Full Name*</Label>
              <Input
                id="name"
                placeholder="John Doe"
                value={newUser.name}
                onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email*</Label>
              <Input
                id="email"
                type="email"
                placeholder="john.doe@example.com"
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
              />
            </div>

            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="password">Password*</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Set user password"
                  value={newUser.password || ""}
                  onChange={(e) => {
                    setNewUser({ ...newUser, password: e.target.value });
                    setShowPasswordRequirements(true);
                  }}
                  onFocus={() => setShowPasswordRequirements(true)}
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {newUser.password && (
                <PasswordStrengthIndicator
                  strength={newPasswordValidation.strength}
                  color={newPasswordValidation.color}
                />
              )}
              {showPasswordRequirements && (
                <PasswordRequirements
                  requirements={passwordRequirements}
                  metRequirements={newPasswordMetRequirements}
                />
              )}
              <p className="text-sm text-muted-foreground mt-1">Admin can set initial password for users</p>
            </div>

            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="confirmPassword">Confirm Password*</Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="Confirm user password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                >
                  {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {confirmPassword && confirmPassword !== newUser.password && (
                <p className="text-sm text-red-500 mt-1">Passwords do not match</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="mobile">Mobile</Label>
              <Input
                id="mobile"
                placeholder="04xx xxx xxx"
                value={newUser.mobile}
                onChange={(e) => setNewUser({ ...newUser, mobile: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="department">Department*</Label>
              <Combobox
                options={departmentOptions}
                value={newUser.department ? String(newUser.department) : undefined}
                onChange={(value) => setNewUser({ ...newUser, department: value || "" })}
                placeholder="Select a department"
                searchPlaceholder="Search departments..."
                className="w-full truncate"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="jobRole">Job Role*</Label>
              <Combobox
                options={jobRoleOptions}
                value={newUser.jobRole || undefined}
                onChange={(value) => {
                  const selectedRole = availableJobRoles.find(role => role.title === value);
                  if (selectedRole) {
                    setNewUser({
                      ...newUser,
                      jobRole: selectedRole.title,
                      department: selectedRole.departmentId
                    });
                  }
                }}
                placeholder="Select a job role"
                searchPlaceholder="Search job roles..."
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="userRole">User Role*</Label>
              <Select
                value={newUser.userRole}
                onValueChange={(value) => setNewUser({ ...newUser, userRole: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a user role" />
                </SelectTrigger>
                <SelectContent>
                  {userRoles.map((role) => (
                    <SelectItem key={role} value={role}>
                      {role}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="manager">
                Manager {newUser.userRole !== "Admin" && <span className="text-destructive">*</span>}
              </Label>
              <PersonSelect
                id="manager"
                people={managerOptions}
                value={newUser.manager ? String(newUser.manager) : ""}
                onChange={(value) => setNewUser({ ...newUser, manager: value || null })}
                disabled={newUser.userRole === "Admin"}
                placeholder={newUser.userRole === "Admin" ? "Not required for Admin" : "Select a manager"}
              />
              {newUser.userRole !== "Admin" && (
                <p className="text-sm text-muted-foreground">Manager is required for all roles except Admin</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="dateOfBirth">Date of Birth</Label>
              <Input
                id="dateOfBirth"
                type="date"
                value={newUser.dateOfBirth || ""}
                onChange={(e) => setNewUser({ ...newUser, dateOfBirth: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="startDate">Start Date <span className="text-destructive">*</span></Label>
              <Input
                id="startDate"
                type="date"
                value={newUser.startDate || ""}
                onChange={(e) => setNewUser({ ...newUser, startDate: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="employmentStatus">Status</Label>
              <Select
                value={newUser.employmentStatus || ""}
                onValueChange={(value) => setNewUser({ ...newUser, employmentStatus: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select employment status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Permanent FT">Permanent FT</SelectItem>
                  <SelectItem value="Permanent PT">Permanent PT</SelectItem>
                  <SelectItem value="Casual">Casual</SelectItem>
                  <SelectItem value="Maximum Term FT">Maximum Term FT</SelectItem>
                  <SelectItem value="Maximum Term PT">Maximum Term PT</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="employmentEntity">Employment Entity</Label>
              <Select
                value={newUser.employmentEntity ? String(newUser.employmentEntity) : ""}
                onValueChange={(value) => setNewUser({ ...newUser, employmentEntity: value ? parseInt(value) : null })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select employment entity" />
                </SelectTrigger>
                <SelectContent>
                  {employmentEntities.map((entity) => (
                    <SelectItem key={entity.id} value={String(entity.id)}>
                      {entity.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="workLocation">Work Location</Label>
              <Select
                value={newUser.workLocation || "none"}
                onValueChange={(value) => setNewUser({ ...newUser, workLocation: value === "none" ? "" : value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select work location" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Not recorded</SelectItem>
                  {WORK_LOCATION_OPTIONS.map((location) => (
                    <SelectItem key={location} value={location}>{location}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="isSubcontractor">Subcontractor</Label>
              <Select
                value={
                  newUser.isSubcontractor === true
                    ? "yes"
                    : newUser.isSubcontractor === false
                      ? "no"
                      : "empty"
                }
                onValueChange={(value) =>
                  setNewUser({
                    ...newUser,
                    isSubcontractor:
                      value === "yes" ? true : value === "no" ? false : null,
                    subcontractorVendorId: value === "yes" ? newUser.subcontractorVendorId : null,
                  })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select subcontractor status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="empty">Not recorded</SelectItem>
                  <SelectItem value="no">No</SelectItem>
                  <SelectItem value="yes">Yes</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {newUser.isSubcontractor && (
              <div className="space-y-2">
                <Label htmlFor="subcontractorVendorId">Subcontractor Name</Label>
                <Combobox
                  options={vendorOptions}
                  value={newUser.subcontractorVendorId ? String(newUser.subcontractorVendorId) : undefined}
                  onChange={(value) =>
                    setNewUser({
                      ...newUser,
                      subcontractorVendorId: value ? parseInt(value) : null,
                    })
                  }
                  placeholder="Select a vendor"
                  searchPlaceholder="Search vendors..."
                  className="w-full truncate"
                />
                {vendors.length === 0 && (
                  <p className="text-xs text-muted-foreground">
                    No vendors are available. Add one under Lists → Vendors, or check your access.
                  </p>
                )}
              </div>
            )}

            <div className="space-y-2 md:col-span-2 border-t border-border pt-4">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
                Demographic Information
              </h3>
              <p className="text-xs text-muted-foreground">
                Optional, self-identified. Used for aggregate HR reporting only; groups under five are suppressed.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="gender">Gender</Label>
              <Select
                value={newUser.gender || "none"}
                onValueChange={(value) => setNewUser({ ...newUser, gender: value === "none" ? "" : value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select gender" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Not recorded</SelectItem>
                  {GENDER_OPTIONS.map((option) => (
                    <SelectItem key={option} value={option}>{option}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="indigenousStatus">Aboriginal and/or Torres Strait Islander</Label>
              <Select
                value={newUser.indigenousStatus || "none"}
                onValueChange={(value) => setNewUser({ ...newUser, indigenousStatus: value === "none" ? "" : value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Not recorded</SelectItem>
                  {INDIGENOUS_STATUS_OPTIONS.map((option) => (
                    <SelectItem key={option} value={option}>{option}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="countryOfBirth">Country of Birth</Label>
              <Select
                value={newUser.countryOfBirth || "none"}
                onValueChange={(value) => setNewUser({ ...newUser, countryOfBirth: value === "none" ? "" : value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select country of birth" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Not recorded</SelectItem>
                  {COUNTRY_OF_BIRTH_OPTIONS.map((option) => (
                    <SelectItem key={option} value={option}>{option}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="disabilityStatus">Disability Status</Label>
              <Select
                value={newUser.disabilityStatus || "none"}
                onValueChange={(value) => setNewUser({ ...newUser, disabilityStatus: value === "none" ? "" : value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select disability status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Not recorded</SelectItem>
                  {DISABILITY_STATUS_OPTIONS.map((option) => (
                    <SelectItem key={option} value={option}>{option}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="residentialAddress">Residential Address</Label>
              <Input
                id="residentialAddress"
                placeholder="123 Main St, Sydney NSW 2000"
                value={newUser.residentialAddress}
                onChange={(e) => setNewUser({ ...newUser, residentialAddress: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="emergencyContactName">Emergency Contact Name</Label>
              <Input
                id="emergencyContactName"
                placeholder="Jane Doe"
                value={newUser.emergencyContactName}
                onChange={(e) => setNewUser({ ...newUser, emergencyContactName: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="emergencyContactNumber">Emergency Contact Number</Label>
              <Input
                id="emergencyContactNumber"
                placeholder="04xx xxx xxx"
                value={newUser.emergencyContactNumber}
                onChange={(e) => setNewUser({ ...newUser, emergencyContactNumber: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="emergencyContactRelationship">Emergency Contact Relationship</Label>
              <Combobox
                options={EMERGENCY_CONTACT_RELATIONSHIP_OPTIONS}
                value={newUser.emergencyContactRelationship || undefined}
                onChange={(value) => setNewUser({ ...newUser, emergencyContactRelationship: value || "" })}
                placeholder="Select relationship"
                searchPlaceholder="Search relationships..."
              />
            </div>

            <div className="space-y-4 md:col-span-2 border-t pt-4">
              <div className="flex items-center space-x-2">
                <Switch
                  id="showInAttendance"
                  checked={newUser.showInAttendance}
                  onCheckedChange={(checked) => setNewUser({ ...newUser, showInAttendance: checked })}
                />
                <Label htmlFor="showInAttendance">Show in Attendance and Leave</Label>
              </div>
              <p className="text-sm text-muted-foreground">
                Enable this if the worker should appear in the Attendance and Leave modules. Recommended for full-time workers. Not recommended for casual workers.
              </p>

              {newUser.showInAttendance && (
                <div className="space-y-2 pt-2">
                  <Label htmlFor="accrualStartDate">Start Accruing From</Label>
                  <Input
                    id="accrualStartDate"
                    type="date"
                    value={newUser.accrualStartDate || ""}
                    onChange={(e) => setNewUser({ ...newUser, accrualStartDate: e.target.value })}
                  />
                  <p className="text-sm text-muted-foreground">
                    Leave will begin accruing from this date. Accruals run daily, based on the person's standard daily hours.
                  </p>
                </div>
              )}
            </div>
          </div>
        </CardContent>
        <CardFooter className="flex justify-end gap-4 border-t px-6 py-4">
          <Button variant="outline" onClick={() => navigate(-1)} disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={handleAdd} disabled={isLoading}>
            {isLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Add Person
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}
